import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  AddCircle,
  ContentCopy,
  Delete,
  Edit,
  OpenInNew,
  Refresh,
  Visibility,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import {
  deleteAdminPreOrderItem,
  getAdminPreOrderItems,
} from '../../services/preOrderService';
import CustomPagination from '../../components/common/CustomPagination';

const statusColors = {
  active: 'success',
  draft: 'default',
  closed: 'warning',
  sold_out: 'error',
  cancelled: 'error',
};

const getShareUrl = (item) => {
  if (item?.share_url) return item.share_url;
  const appHost = window.location.hostname.startsWith('test.')
    ? 'test.app.checkstall.com'
    : 'app.checkstall.com';
  return `https://${appHost}/share/preOrder/${item.slug}`;
};

const PreOrderManagePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const totalPages = Math.max(1, Math.ceil(items.length / rowsPerPage));
  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return items.slice(start, start + rowsPerPage);
  }, [items, page, rowsPerPage]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAdminPreOrderItems();
      setItems(response.items || []);
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.message || 'Failed to load pre-order items.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, totalPages - 1));
  }, [totalPages]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    if (event.target.value === 'custom') {
      setCustomRowsInput(String(rowsPerPage));
      setCustomRowsActive(true);
      return;
    }
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleCustomRowsInputChange = (event) => {
    setCustomRowsInput(event.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const value = Number(customRowsInput);
    if (value > 0) {
      setRowsPerPage(value);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const copyShareLink = async (item) => {
    try {
      await navigator.clipboard.writeText(getShareUrl(item));
      setSnackbar({ open: true, severity: 'success', message: 'Share link copied.' });
    } catch {
      setSnackbar({ open: true, severity: 'error', message: 'Could not copy the share link.' });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await deleteAdminPreOrderItem(deleteTarget.id);
      setItems((previous) => previous.filter((item) => item.id !== deleteTarget.id));
      setDeleteTarget(null);
      setSnackbar({ open: true, severity: 'success', message: response.message || 'Pre-order deleted.' });
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.message || 'Failed to delete pre-order item.',
      });
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(() => [
    {
      field: 'pic_url',
      headerName: 'Item',
      width: 88,
      sortable: false,
      renderCell: ({ row }) => <Avatar src={row.pic_url} variant="rounded" sx={{ width: 52, height: 52 }} />,
    },
    { field: 'title', headerName: 'Title', minWidth: 260, flex: 1 },
    {
      field: 'final_price',
      headerName: 'Final Price',
      width: 130,
      renderCell: ({ row }) => `CNY ${Number(row.final_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
    },
    {
      field: 'stock',
      headerName: 'Stock',
      width: 125,
      valueGetter: (_value, row) => row.total_stock ? `${row.available_quantity ?? 0}/${row.total_stock}` : 'Unlimited',
    },
    { field: 'sold_quantity', headerName: 'Sold', width: 90 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: ({ value }) => (
        <Chip label={String(value || '').replace('_', ' ')} color={statusColors[value] || 'default'} size="small" />
      ),
    },
    {
      field: 'order_count',
      headerName: 'Orders',
      width: 95,
      renderCell: ({ value }) => Number(value || 0),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 170,
      renderCell: ({ value }) => value ? new Date(value).toLocaleString() : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.25}>
          <Tooltip title="View details">
            <IconButton size="small" color="primary" onClick={() => setSelectedItem(row)}><Visibility /></IconButton>
          </Tooltip>
          <Tooltip title="Edit pre-order">
            <IconButton size="small" color="warning" onClick={() => navigate(`/admin/pre-order/edit/${row.id}`)}><Edit /></IconButton>
          </Tooltip>
          <Tooltip title="Copy share link">
            <IconButton size="small" onClick={() => copyShareLink(row)}><ContentCopy /></IconButton>
          </Tooltip>
          <Tooltip title="Open customer page">
            <IconButton size="small" color="success" component="a" href={getShareUrl(row)} target="_blank" rel="noreferrer"><OpenInNew /></IconButton>
          </Tooltip>
          <Tooltip title={row.order_count ? 'Items with order history cannot be deleted' : 'Delete pre-order'}>
            <span>
              <IconButton size="small" color="error" disabled={Boolean(row.order_count)} onClick={() => setDeleteTarget(row)}><Delete /></IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ], [navigate]);

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden',
    }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 7 }}>
          <Typography variant="h4" fontWeight="bold" color="primary.main">Manage Pre-orders</Typography>
          <Typography variant="body2" color="text.secondary">View, update, share, close, or delete manually created pre-order items.</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 5 }}>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" startIcon={<Refresh />} onClick={loadItems} disabled={loading}>Refresh</Button>
            <Button variant="contained" startIcon={<AddCircle />} onClick={() => navigate('/admin/pre-order/create')}>Create Pre-order</Button>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card sx={{ overflow: 'hidden' }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <DataGrid
                rows={paginatedItems}
                columns={columns}
                loading={loading}
                disableRowSelectionOnClick
                hideFooter
                rowHeight={72}
                sx={{
                  border: 0,
                  minHeight: 560,
                  width: '100%',
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 'bold',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: theme.palette.primary.contrastText,
                    fontWeight: 'bold',
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: theme.palette.primary.contrastText,
                  },
                  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                    outline: 'none',
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <CustomPagination
            page={page}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            customRowsActive={customRowsActive}
            customRowsInput={customRowsInput}
            onCustomRowsInputChange={handleCustomRowsInputChange}
            onCustomRowsInputBlur={handleCustomRowsInputBlur}
          />
        </Grid>
      </Grid>

      <Dialog open={Boolean(selectedItem)} onClose={() => setSelectedItem(null)} maxWidth="md" fullWidth>
        <DialogTitle>Pre-order Details</DialogTitle>
        <DialogContent dividers>
          {selectedItem && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Avatar src={selectedItem.pic_url} variant="rounded" sx={{ width: 140, height: 140 }} />
                <Box>
                  <Typography variant="h6">{selectedItem.title}</Typography>
                  <Chip label={selectedItem.status.replace('_', ' ')} color={statusColors[selectedItem.status] || 'default'} size="small" sx={{ my: 1 }} />
                  <Typography>Final price: CNY {Number(selectedItem.final_price || 0).toFixed(2)}</Typography>
                  <Typography>Available: {selectedItem.total_stock ? `${selectedItem.available_quantity}/${selectedItem.total_stock}` : 'Unlimited'}</Typography>
                  <Typography>Customer orders: {selectedItem.order_count || 0}</Typography>
                </Box>
              </Stack>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{selectedItem.description || 'No description.'}</Typography>
              <Alert severity="info">{getShareUrl(selectedItem)}</Alert>
              <Typography variant="subtitle2">Specifications: {selectedItem.specifications?.length || 0}</Typography>
              <Typography variant="subtitle2">Generated SKUs: {selectedItem.variants?.length || 0}</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => copyShareLink(selectedItem)} startIcon={<ContentCopy />}>Copy Link</Button>
          <Button onClick={() => navigate(`/admin/pre-order/edit/${selectedItem?.id}`)} startIcon={<Edit />}>Edit</Button>
          <Button onClick={() => setSelectedItem(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Pre-order?</DialogTitle>
        <DialogContent dividers>
          <Typography>Delete <strong>{deleteTarget?.title}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleting} startIcon={deleting ? <CircularProgress size={18} /> : <Delete />}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PreOrderManagePage;
